/**
 * The club-admin walkthrough: what every page is for, in the order someone
 * actually needs them.
 *
 * Setup first (sports → coaches → teams → athletes), because nothing else
 * works before that. Then the daily work, then money, then the extras. A tour
 * that opened with "affiliate programme" would lose the reader on step two.
 *
 * `selector` is a hint, not a requirement: if the element is not on the page
 * the card simply centres itself. Tying each step to a specific DOM node would
 * make the tour break every time a page is restyled.
 */

export type TourStep = {
  key: string;
  path: string;
  title: string;
  body: string;
  selector?: string;
};

export type TourContext = {
  /** Whether the club has turned coordinators on in its settings. */
  coordinators: boolean;
};

const BASE_TOUR: TourStep[] = [
  {
    key: "welcome",
    path: "/dashboard/admin-club",
    title: "Bun venit în iGROWth",
    body: "Îți arăt în câteva pași la ce folosește fiecare pagină. Durează sub două minute și poți ieși oricând — turul se reia de la butonul „Tur ghidat”.",
  },
  {
    key: "overview",
    path: "/dashboard/admin-club",
    title: "Prezentare generală",
    body: "Pagina de start a clubului: câți sportivi și antrenori ai, antrenamentele apropiate și starea facturilor. De aici pornești spre orice altă secțiune.",
  },
  {
    key: "settings",
    path: "/dashboard/admin-club/settings",
    title: "Setări club — începe aici",
    body: "Datele clubului și, cel mai important la început, sporturile practicate. Echipele și grupele de vârstă se construiesc pe baza lor, deci acesta e primul pas.",
  },
  {
    key: "coaches",
    path: "/dashboard/admin-club/coaches",
    title: "Antrenori",
    body: "Îi inviți pe email. Antrenorul își face singur contul și vede doar echipele pe care i le atribui — prezență, punctaje, metrici.",
  },
  {
    key: "teams",
    path: "/dashboard/admin-club/teams",
    title: "Echipe",
    body: "Grupele clubului. Fiecare are un sport, o grupă de vârstă și un antrenor. Sportivii se adaugă în echipe, nu direct în club.",
  },
  {
    key: "athletes",
    path: "/dashboard/admin-club/athletes",
    title: "Sportivi",
    body: "Îi adaugi manual sau prin invitație. Dacă nu ai emailul copilului — cazul obișnuit la minori — trimiți invitația părintelui, cu numele copilului: el creează contul, iar copilul intră direct în echipa aleasă de tine.",
  },
  {
    key: "sessions",
    path: "/dashboard/admin-club/sessions",
    title: "Antrenamente",
    body: "Programezi ședințele pe echipe, inclusiv recurente. Antrenorul face prezența din aplicație, iar sportivii primesc puncte pentru participare.",
  },
  {
    key: "calendar",
    path: "/dashboard/admin-club/calendar",
    title: "Calendar",
    body: "Toate antrenamentele și evenimentele clubului într-un singur loc, ca să vezi suprapunerile înainte să apară.",
  },
  {
    key: "locations",
    path: "/dashboard/admin-club/locations",
    title: "Locații",
    body: "Sălile și terenurile unde se ține antrenamentul. Le alegi când programezi o ședință, ca părinții să știe unde vin.",
  },
  {
    key: "events",
    path: "/dashboard/admin-club/events",
    title: "Evenimente",
    body: "Competiții, cantonamente, ședințe cu părinții. Trimiți invitații și vezi cine a confirmat.",
  },
  {
    key: "leaderboard",
    path: "/dashboard/admin-club/leaderboard",
    title: "Clasament",
    body: "Clasamentul pe puncte al sportivilor din club. Punctele vin din prezență, quiz-uri și obiective îndeplinite.",
  },
  {
    key: "quizzes",
    path: "/dashboard/admin-club/quizzes",
    title: "Quiz-uri",
    body: "Conținut educativ pentru sportivi. Le aloci pe echipe sau grupe de vârstă, iar ei primesc puncte când răspund corect.",
  },
  {
    key: "invoices",
    path: "/dashboard/admin-club/invoices",
    title: "Facturi",
    body: "Facturile pe care le primești de la platformă, cu starea plății fiecăreia.",
  },
  {
    key: "receivables",
    path: "/dashboard/admin-club/receivables",
    title: "Încasări",
    body: "Ce ai de încasat de la sportivi: cine a plătit cotizația, cine a rămas în urmă și cât. În tabul „Numerar la antrenori\" vezi banii cash strânși de fiecare antrenor și înregistrezi predarea lor.",
  },
  {
    key: "products",
    path: "/dashboard/admin-club/products",
    title: "Produse și servicii",
    body: "Cotizații, echipament, taxe de participare. Le atașezi sportivilor și devin sume de încasat.",
  },
  {
    key: "affiliate",
    path: "/dashboard/admin-club/affiliate",
    title: "Program de afiliere",
    body: "Linkul tău de recomandare și comisioanele câștigate când cluburile sau sportivii aduși de tine trec pe un plan plătit.",
  },
  {
    key: "support",
    path: "/dashboard/admin-club/support",
    title: "Suport",
    body: "Deschizi un tichet și îți răspundem. Asta e și locul din care ceri ajutor dacă ceva nu merge.",
  },
  {
    key: "done",
    path: "/dashboard/admin-club",
    title: "Gata",
    body: "Ai văzut tot. Dacă abia începi, urmează lista „Primii pași” de pe pagina de start: sporturi, antrenori, echipe, sportivi — în ordinea asta. Turul se reia oricând din butonul în formă de bec, din bara de sus.",
  },
];

/**
 * The coordinator steps, which only some clubs ever see.
 *
 * A coordinator runs one sport across several teams, and small clubs have no
 * such person — so the role is off by default and the tour follows the club's
 * own setting. Teaching a one-coach club about coordinators would be teaching
 * a screen they do not have.
 *
 * When it is off we still say the option exists, once, on the page where it is
 * turned on: someone who never hears of it cannot decide they need it.
 */
const COORDINATORS_ON: TourStep = {
  key: "staff",
  path: "/dashboard/admin-club/staff",
  title: "Coordonatori",
  body: "Coordonatorul răspunde de un sport din club: vede și gestionează doar echipele, antrenorii și sportivii sportului său și poate invita el însuși antrenori — antrenorii invitați de el primesc automat sportul lui. Un sport poate avea mai mulți coordonatori, iar un coordonator poate acoperi mai multe sporturi.",
};

const COORDINATORS_OFF: TourStep = {
  key: "staff-hint",
  path: "/dashboard/admin-club/settings",
  title: "Coordonatori (opțional)",
  body: "Dacă ai un club mare, poți activa din Setări rolul de Coordonator: o persoană care răspunde de un singur sport — vede doar echipele, antrenorii și sportivii sportului ei și poate invita antrenori în locul tău. Cluburile mici nu au nevoie de el, de aceea opțiunea e oprită implicit.",
};

/**
 * Build the walkthrough for a particular club.
 *
 * The tour is assembled rather than fixed because a step that opens a page the
 * club does not use leaves the reader on an empty screen, which teaches the
 * opposite of what a tour is for.
 */
export function buildClubAdminTour(ctx: TourContext): TourStep[] {
  const steps = [...BASE_TOUR];
  // Right after Setări, where sports are picked: coordinators are assigned per
  // sport, so this is the moment it makes sense.
  const at = steps.findIndex((s) => s.key === "settings");
  const insertAt = at === -1 ? 0 : at + 1;
  steps.splice(insertAt, 0, ctx.coordinators ? COORDINATORS_ON : COORDINATORS_OFF);
  return steps;
}
